import { useEffect, useRef, useState } from "react";
import { Save, Upload } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { api, uploadUrl, formatApiErrorDetail } from "../lib/api";

const inputCls =
  "w-full bg-white border border-ink/15 px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-bronzo transition-colors duration-300";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50 mb-2";

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => setForm({}));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/settings/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("logo_url", r.data.logo_url);
      toast.success("Logo caricato — salva le impostazioni per confermare");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!form) return <div data-testid="settings-loading" />;

  return (
    <div data-testid="admin-settings" className="max-w-3xl">
      <Seo title="Impostazioni | V32 Admin" noindex />
      <h1 className="font-display font-light text-3xl text-ink">Impostazioni</h1>
      <p className="mt-2 text-sm text-ink/60">
        I campi vuoti non vengono mostrati sul sito pubblico: compila solo i dati reali e verificati.
      </p>

      <div className="mt-10 space-y-10">
        <section className="bg-white border border-ink/10 p-6 lg:p-8 space-y-5" data-testid="settings-contact">
          <h2 className="font-display font-medium text-lg text-ink">Contatti aziendali</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="s-phone" className={labelCls}>Numero di telefono</label>
              <input id="s-phone" data-testid="settings-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div>
              <label htmlFor="s-email" className={labelCls}>Email aziendale (pubblica)</label>
              <input id="s-email" data-testid="settings-email" type="email" className={inputCls} placeholder="Da configurare" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div>
              <label htmlFor="s-wa" className={labelCls}>WhatsApp Business (con prefisso, es. +4179…)</label>
              <input id="s-wa" data-testid="settings-whatsapp" className={inputCls} value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} />
            </div>
            <div>
              <label htmlFor="s-notify" className={labelCls}>Email per le notifiche delle richieste</label>
              <input id="s-notify" data-testid="settings-notification-email" type="email" className={inputCls} placeholder="Riceverai qui le nuove richieste" value={form.notification_email} onChange={(e) => set("notification_email", e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="s-address" className={labelCls}>Sede / indirizzo pubblico</label>
              <input id="s-address" data-testid="settings-address" className={inputCls} placeholder="Da confermare — lascia vuoto finché non è definitivo" value={form.address} onChange={(e) => set("address", e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="s-maps" className={labelCls}>Link Google Maps (facoltativo)</label>
              <input id="s-maps" data-testid="settings-maps" className={inputCls} value={form.maps_url} onChange={(e) => set("maps_url", e.target.value)} />
            </div>
          </div>
        </section>

        <section className="bg-white border border-ink/10 p-6 lg:p-8 space-y-5" data-testid="settings-legal">
          <h2 className="font-display font-medium text-lg text-ink">Dati legali</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="s-legal" className={labelCls}>Ragione sociale completa</label>
              <input id="s-legal" data-testid="settings-legal-name" className={inputCls} placeholder="Da confermare" value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} />
            </div>
            <div>
              <label htmlFor="s-vat" className={labelCls}>Numero IVA / IDE</label>
              <input id="s-vat" data-testid="settings-vat" className={inputCls} placeholder="CHE-…" value={form.vat} onChange={(e) => set("vat", e.target.value)} />
            </div>
          </div>
        </section>

        <section className="bg-white border border-ink/10 p-6 lg:p-8 space-y-5" data-testid="settings-social">
          <h2 className="font-display font-medium text-lg text-ink">Social</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <label htmlFor="s-ig" className={labelCls}>Instagram (URL)</label>
              <input id="s-ig" data-testid="settings-instagram" className={inputCls} value={form.instagram} onChange={(e) => set("instagram", e.target.value)} />
            </div>
            <div>
              <label htmlFor="s-fb" className={labelCls}>Facebook (URL)</label>
              <input id="s-fb" data-testid="settings-facebook" className={inputCls} value={form.facebook} onChange={(e) => set("facebook", e.target.value)} />
            </div>
            <div>
              <label htmlFor="s-li" className={labelCls}>LinkedIn (URL)</label>
              <input id="s-li" data-testid="settings-linkedin" className={inputCls} value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} />
            </div>
          </div>
        </section>

        <section className="bg-white border border-ink/10 p-6 lg:p-8 space-y-5" data-testid="settings-branding">
          <h2 className="font-display font-medium text-lg text-ink">Logo</h2>
          <div className="flex items-center gap-6">
            <div className="w-40 h-20 bg-antracite flex items-center justify-center overflow-hidden">
              {form.logo_url ? (
                <img src={uploadUrl(form.logo_url)} alt="Logo V32 caricato" className="max-h-14 max-w-[9rem] object-contain" />
              ) : (
                <span className="font-display font-semibold text-avorio text-xl">
                  V32 <span className="font-mono text-[8px] uppercase tracking-[0.3em] text-pietra">Group</span>
                </span>
              )}
            </div>
            <div>
              <button
                onClick={() => logoRef.current?.click()}
                data-testid="settings-logo-upload"
                className="inline-flex items-center gap-2 bg-white border border-ink/20 text-sm px-4 py-2.5 hover:border-bronzo hover:text-bronzo transition-colors duration-300"
              >
                <Upload className="w-4 h-4" /> Carica logo (SVG, PNG, JPG)
              </button>
              <input ref={logoRef} type="file" accept="image/svg+xml,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} />
              <p className="mt-2 text-xs text-ink/40">Se non carichi un logo, viene usata la versione tipografica.</p>
            </div>
          </div>
        </section>

        <section className="bg-white border border-ink/10 p-6 lg:p-8 space-y-5" data-testid="settings-founder">
          <h2 className="font-display font-medium text-lg text-ink">Fondatore (pagina Chi siamo)</h2>
          <p className="text-xs text-ink/40">La sezione appare sul sito solo quando nome e foto sono compilati.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="s-fname" className={labelCls}>Nome e cognome</label>
              <input id="s-fname" data-testid="settings-founder-name" className={inputCls} value={form.founder_name} onChange={(e) => set("founder_name", e.target.value)} />
            </div>
            <div>
              <label htmlFor="s-fphoto" className={labelCls}>Foto (percorso upload o vuoto)</label>
              <input id="s-fphoto" data-testid="settings-founder-photo" className={inputCls} placeholder="Es. branding/founder.jpg" value={form.founder_photo} onChange={(e) => set("founder_photo", e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="s-ftext" className={labelCls}>Presentazione</label>
              <textarea id="s-ftext" data-testid="settings-founder-text" rows={4} className={inputCls} value={form.founder_text} onChange={(e) => set("founder_text", e.target.value)} />
            </div>
          </div>
        </section>

        <button
          onClick={save}
          disabled={saving}
          data-testid="settings-save"
          className="inline-flex items-center gap-2 bg-antracite text-avorio font-medium px-8 py-3.5 hover:bg-bronzo disabled:opacity-50 transition-colors duration-300"
        >
          <Save className="w-4 h-4" /> {saving ? "Salvataggio…" : "Salva impostazioni"}
        </button>
      </div>
    </div>
  );
}
